import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, LL_V_L, SimpleListAdapter, ThunderDispatcher, TS_CheckboxGroup, TS_DropDown } from '@nu-art/thunderstorm-frontend';
import { Day, filterDuplicates } from '@nu-art/ts-common';
import { ModuleFE_EHRInstallation, ModuleFE_Environment, ModuleFE_Facility, ModuleFE_Organization } from '@app/org-frontend';
export const TimeRangeOptions = [
    { id: 'all', label: 'All Time' },
    { id: '12h', label: 'Last 12 hours', duration: Day / 2 },
    { id: '24h', label: 'Last 24 hours', duration: Day },
    { id: '3d', label: 'Last 2 days', duration: Day * 3 },
    { id: '7d', label: 'Last Week', duration: Day * 7 },
];
export const dispatch_OnEHREmulatorFiltersChange = new ThunderDispatcher('__onEHREmulatorFiltersChange');
export class Dialog_EHREmulatorFilters extends ComponentSync {
    //######################### Life Cycle #########################
    __onEHREmulatorFiltersChange = (data) => {
        this.setState({
            selectedFacilityIds: data.selectedFacilityIds,
            selectedEnvironmentIds: data.selectedEnvironmentIds,
            search: data.search,
            selectedTimeRange: data.selectedTimeRange,
        });
    };
    deriveStateFromProps(nextProps, state) {
        state.selectedFacilityIds ??= nextProps.selectedFacilityIds;
        state.selectedEnvironmentIds ??= nextProps.selectedEnvironmentIds;
        state.search ??= nextProps.search;
        state.selectedTimeRange ??= nextProps.selectedTimeRange;
        // Load data from cache
        if (!state.organizations) {
            state.organizations = [...ModuleFE_Organization.cache.all()];
        }
        if (!state.facilities) {
            state.facilities = [...ModuleFE_Facility.cache.all()];
        }
        if (!state.environments) {
            state.environments = [...ModuleFE_Environment.cache.all()];
        }
        return state;
    }
    //######################### Logic #########################
    getOrganizationToFacilitiesMap = () => {
        const organizations = this.state.organizations ?? [];
        const facilities = this.state.facilities ?? [];
        return organizations.reduce((acc, organization) => {
            const orgFacilities = facilities.filter(facility => facility.organizationId === organization._id);
            if (orgFacilities.length > 0) {
                acc.push({ facilities: orgFacilities, organization });
            }
            return acc;
        }, []);
    };
    handleFacilitySelectionChange = (selectedFacilityIds, idScope) => {
        const currentSelectedFacilityIds = this.state.selectedFacilityIds ?? [];
        const facilitiesFromOtherOrgs = currentSelectedFacilityIds.filter((facilityId) => !idScope.includes(facilityId));
        const updatedFacilityIds = filterDuplicates([...facilitiesFromOtherOrgs, ...selectedFacilityIds]);
        dispatch_OnEHREmulatorFiltersChange.dispatchUI({
            selectedFacilityIds: updatedFacilityIds,
            selectedEnvironmentIds: this.state.selectedEnvironmentIds,
            search: this.state.search,
            selectedTimeRange: this.state.selectedTimeRange,
        });
    };
    handleEnvironmentSelectionChange = (selectedIds) => {
        dispatch_OnEHREmulatorFiltersChange.dispatchUI({
            selectedFacilityIds: this.state.selectedFacilityIds,
            selectedEnvironmentIds: selectedIds,
            search: this.state.search,
            selectedTimeRange: this.state.selectedTimeRange,
        });
    };
    handleTimeRangeChange = (timeRange) => {
        dispatch_OnEHREmulatorFiltersChange.dispatchUI({
            selectedFacilityIds: this.state.selectedFacilityIds,
            selectedEnvironmentIds: this.state.selectedEnvironmentIds,
            search: this.state.search,
            selectedTimeRange: timeRange,
        });
    };
    onResetFilters = () => {
        dispatch_OnEHREmulatorFiltersChange.dispatchUI({
            selectedFacilityIds: [],
            selectedEnvironmentIds: [],
            search: '',
            selectedTimeRange: undefined,
        });
    };
    //######################### Render #########################
    renderTimeFilter = () => {
        const adapter = SimpleListAdapter(TimeRangeOptions, item => (_jsx("div", { className: 'node-data', children: item.item.label })));
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'ehr-emulator__filters__title', children: "Time Range" }), _jsx(TS_DropDown, { adapter: adapter, selected: this.state.selectedTimeRange, onSelected: this.handleTimeRangeChange, placeholder: 'Select time range', canUnselect: true, className: 'ehr-emulator__time-filter-dropdown' })] }));
    };
    renderOrganizations = () => {
        const organizationsToFacilities = this.getOrganizationToFacilitiesMap();
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'ehr-emulator__filters__title', children: "Facilities" }), organizationsToFacilities.map(option => {
                    const parent = { id: option.organization._id, label: option.organization.name };
                    const children = option.facilities.map((facility) => ({ id: facility._id, label: facility.name }));
                    const idScope = children.map(child => child.id);
                    const selectedFacilityIdsForOrg = option.facilities
                        .filter(facility => this.state.selectedFacilityIds.includes(facility._id))
                        .map(facility => facility._id);
                    return (_jsx(TS_CheckboxGroup, { parent: parent, options: children, selectedIds: selectedFacilityIdsForOrg, onChange: selectedIds => this.handleFacilitySelectionChange(selectedIds, idScope) }, parent.id));
                })] }));
    };
    renderEnvironments = () => {
        const environments = this.state.environments?.map((environment) => {
            const installation = ModuleFE_EHRInstallation.cache.unique(environment.ehrInstallationId);
            const organization = ModuleFE_Organization.cache.unique(installation?.organizationId);
            return ({ id: environment._id, label: `${organization?.alias ?? 'N/A'} - ${installation?.label ?? 'N/A'} - ${environment.label}` });
        });
        return (_jsxs(_Fragment, { children: [_jsx("div", { className: 'ehr-emulator__filters__title', children: "Environments" }), environments.length > 0 && (_jsx(TS_CheckboxGroup, { parent: { id: 'environments', label: 'All Environments' }, options: environments, selectedIds: this.state.selectedEnvironmentIds, onChange: (selectedIds) => this.handleEnvironmentSelectionChange(selectedIds) }))] }));
    };
    render() {
        return (_jsxs(LL_V_L, { className: 'ehr-emulator__filters-dialog', children: [_jsx("div", { className: 'ehr-emulator__filters__title', children: "Filters" }), this.renderTimeFilter(), _jsx("div", { className: 'ehr-emulator__filters__separator' }), this.renderOrganizations(), _jsx("div", { className: 'ehr-emulator__filters__separator' }), this.renderEnvironments(), _jsx(Button, { onClick: () => this.onResetFilters(), children: "Reset Filters" })] }));
    }
}
//# sourceMappingURL=Dialog_EHREmulatorFilters.js.map